export const AI_MODELS = {
    highCapability: "gemini-3.5-flash",
    routine: "gemini-3.5-flash-lite",
    lightweight: "gemini-3.1-flash-lite",
    preview: "gemini-3.1-pro-preview"
} as const;

export const AI_MODEL_LIFECYCLE = [
    {
        id: AI_MODELS.highCapability,
        tier: "High capability",
        usedFor: "Country and threat analysis, intelligence reports, and complex security plans",
        earliestVertexRetirement: "2027-05-19"
    },
    {
        id: AI_MODELS.routine,
        tier: "Routine",
        usedFor: "General AI requests, assistant responses, and support chat summaries",
        earliestVertexRetirement: "2027-07-21"
    },
    {
        id: AI_MODELS.lightweight,
        tier: "Lightweight",
        usedFor: "Classification, data extraction, and helpdesk triage",
        earliestVertexRetirement: "2027-05-07"
    }
] as const;

export const AI_MODEL_RETIREMENT_SOURCE = "https://docs.cloud.google.com/gemini-enterprise-agent-platform/models/model-versions";

export const VERTEX_AI_LOCATION = "global";
export const VERTEX_AI_API_ENDPOINT = "aiplatform.googleapis.com";

export function getVertexPublisherModelPath(projectId: string, model: string): string {
    return `projects/${projectId}/locations/${VERTEX_AI_LOCATION}/publishers/google/models/${model}`;
}

export function getVertexLocationParent(projectId: string): string {
    return `projects/${projectId}/locations/${VERTEX_AI_LOCATION}`;
}

export type AiModelTier = "highCapability" | "routine" | "lightweight";

export interface AiModelRoute {
    tier: AiModelTier;
    primaryModel: string;
    fallbackModels: string[];
    routingEnabled: boolean;
    fallbackEnabled: boolean;
}

interface AiRouteInput {
    workflow?: string;
    requestedModel?: string;
}

const HIGH_CAPABILITY_WORKFLOWS = new Set([
    "advanced route analysis & ep plan",
    "audit remediation execution",
    "comprehensive country geopolitical & risk analysis",
    "country_geopolitical_analysis",
    "daily_country_analysis",
    "ep advance agent site survey report",
    "ep pre-movement intelligence brief",
    "ep threat analysis",
    "executive_report",
    "ep_ops_risk_analysis",
    "ep_threat_analysis",
    "event security plan",
    "event_security_plan",
    "facility and asset protection plan",
    "generate country analysis",
    "global threat group analysis",
    "integrated intelligence",
    "intelligence_analysis",
    "integrated threat analysis",
    "manual_country_analysis",
    "risk_event_verification",
    "service_evidence_revision",
    "security & ep operations risk analysis",
    "staff protection plan",
    "staff_protection_plan",
    "signal_verification",
    "travel security brief",
    "trm-01 workspace intelligence",
    "travel_security_brief"
]);

const LIGHTWEIGHT_WORKFLOWS = new Set([
    "classification",
    "data_extraction",
    "helpdesk_triage",
    "source_normalization"
]);

const STABLE_MODELS = new Set<string>([
    AI_MODELS.highCapability,
    AI_MODELS.routine,
    AI_MODELS.lightweight
]);

const PREVIEW_MODELS = new Set<string>([AI_MODELS.preview]);

function featureEnabled(value: string | undefined, defaultValue: boolean): boolean {
    if (value === undefined)
        return defaultValue;
    return value.trim().toLowerCase() === "true";
}

function normalizeWorkflow(workflow?: string): string {
    return workflow?.trim().toLowerCase() || "legacy";
}

function requestedTier(requestedModel?: string): AiModelTier | null {
    const model = requestedModel?.trim().toLowerCase();
    if (!model)
        return null;
    if (model.includes("pro") || model === AI_MODELS.highCapability)
        return "highCapability";
    if (model.includes("lite"))
        return "lightweight";
    return "routine";
}

export function getWorkflowTier(workflow?: string, requestedModel?: string): AiModelTier {
    const normalizedWorkflow = normalizeWorkflow(workflow);
    if (HIGH_CAPABILITY_WORKFLOWS.has(normalizedWorkflow))
        return "highCapability";
    if (LIGHTWEIGHT_WORKFLOWS.has(normalizedWorkflow))
        return "lightweight";
    return requestedTier(requestedModel) || "routine";
}

function validatedModel(value: string | undefined, fallback: string, previewsEnabled: boolean): string {
    const model = value?.trim();
    if (!model)
        return fallback;
    if (STABLE_MODELS.has(model))
        return model;
    if (previewsEnabled && PREVIEW_MODELS.has(model))
        return model;
    return fallback;
}

export function resolveAiModelRoute(
    input: AiRouteInput,
    environment: NodeJS.ProcessEnv = process.env
): AiModelRoute {
    const routingEnabled = featureEnabled(environment.AI_MODEL_ROUTING_ENABLED, true);
    const fallbackEnabled = featureEnabled(environment.AI_MODEL_FALLBACK_ENABLED, true);
    const previewsEnabled = featureEnabled(environment.AI_PREVIEW_MODELS_ENABLED, false);
    const tier = routingEnabled
        ? getWorkflowTier(input.workflow, input.requestedModel)
        : requestedTier(input.requestedModel) || "routine";

    const configuredModels: Record<AiModelTier, string> = {
        highCapability: validatedModel(
            environment.AI_MODEL_HIGH_CAPABILITY,
            AI_MODELS.highCapability,
            previewsEnabled
        ),
        routine: validatedModel(environment.AI_MODEL_ROUTINE, AI_MODELS.routine, previewsEnabled),
        lightweight: validatedModel(
            environment.AI_MODEL_LIGHTWEIGHT,
            AI_MODELS.lightweight,
            previewsEnabled
        )
    };

    const primaryModel = configuredModels[tier];
    const fallbackCandidate = tier === "highCapability"
        ? configuredModels.routine
        : tier === "routine"
            ? configuredModels.lightweight
            : configuredModels.routine;
    const configuredFallback = validatedModel(
        environment.AI_MODEL_FALLBACK,
        fallbackCandidate,
        previewsEnabled
    );
    const fallbackModels = fallbackEnabled && configuredFallback !== primaryModel
        ? [configuredFallback]
        : [];

    return { tier, primaryModel, fallbackModels, routingEnabled, fallbackEnabled };
}

export function isTransientAiError(error: unknown): boolean {
    const candidate = error as { code?: number | string; status?: number | string; message?: string };
    const status = Number(candidate?.code || candidate?.status);
    if ([408, 429, 500, 502, 503, 504].includes(status))
        return true;
    const message = candidate?.message?.toLowerCase() || "";
    return /deadline|rate limit|resource exhausted|temporar|timeout|unavailable/.test(message);
}

export async function executeWithModelFallback<T>(
    route: AiModelRoute,
    operation: (model: string) => Promise<T>
): Promise<{ result: T; model: string; fallbackUsed: boolean }> {
    const models = [route.primaryModel, ...route.fallbackModels];
    let lastError: unknown;
    for (let index = 0; index < models.length; index++) {
        try {
            return {
                result: await operation(models[index]),
                model: models[index],
                fallbackUsed: index > 0
            };
        } catch (error) {
            lastError = error;
            if (!isTransientAiError(error) || index === models.length - 1)
                throw error;
        }
    }
    throw lastError;
}
